import React, { useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  ChartBarIcon,
  LightBulbIcon,
  CpuChipIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];
const PLATFORM_COLORS = {
  chatgpt: '#10b981',
  googleAI: '#3b82f6',
  perplexity: '#8b5cf6',
  bingChat: '#06b6d4',
  claude: '#f97316',
  you: '#ec4899'
};

const PLATFORM_NAMES = {
  chatgpt: 'ChatGPT',
  googleAI: 'Google AI',
  perplexity: 'Perplexity',
  bingChat: 'Bing Chat',
  claude: 'Claude',
  you: 'You.com'
};

function ScoreGauge({ score, label, size = 'lg' }) {
  const radius = size === 'lg' ? 60 : 40;
  const stroke = size === 'lg' ? 8 : 6;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = score >= 70 ? '#10b981' : score >= 40 ? '#f59e0b' : '#ef4444';
  const dim = (radius + stroke) * 2;

  return (
    <div className="flex flex-col items-center">
      <svg width={dim} height={dim} className="transform -rotate-90">
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke="#374151" strokeWidth={stroke} />
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circumference} strokeDashoffset={circumference - progress} strokeLinecap="round"
          className="transition-all duration-1000 ease-out" />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: dim, height: dim }}>
        <span className={`font-bold ${size === 'lg' ? 'text-3xl' : 'text-xl'} text-gray-900`}>{score}</span>
        <span className="text-gray-600 text-xs">/100</span>
      </div>
      {label && <span className="mt-2 text-sm text-gray-600">{label}</span>}
    </div>
  );
}

function TrendIcon({ trend }) {
  if (trend === 'up') return <ArrowTrendingUpIcon className="w-4 h-4 text-green-400" />;
  if (trend === 'down') return <ArrowTrendingDownIcon className="w-4 h-4 text-red-400" />;
  return <MinusIcon className="w-4 h-4 text-gray-500" />;
}

const AISearchVisibility = ({ domain, scanData, snapshotData }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!snapshotData) return null;

  const { overallScore, platformScores, visibilityMetrics, keywordVisibility, contentOptimization, recommendations, serpPresence, rankingKeywordsSummary } = snapshotData;

  // Prepare radar chart data for platform scores
  // All platforms stay listed. A missing score renders as "Not available",
  // never as 0 - there is no interface that measures these.
  const PLATFORM_ROW_COLORS = {
    chatgpt: '#10b981', googleAI: '#3b82f6', perplexity: '#8b5cf6',
    bingChat: '#06b6d4', claude: '#f97316', you: '#ec4899'
  };
  const platformRows = Object.keys(PLATFORM_NAMES).map((key) => ({
    key,
    name: PLATFORM_NAMES[key],
    score: typeof (platformScores || {})[key] === 'number' ? platformScores[key] : null
  }));

  // Prepare bar chart data for visibility metrics
  // Only chart metrics that were actually measured. Anything the API returns
  // as null is omitted rather than plotted at 0.
  const metricsBarData = [
    { name: 'Content Relevance', value: visibilityMetrics?.contentRelevance, fill: '#f97316' },
    { name: 'Authority', value: visibilityMetrics?.authorityScore, fill: '#3b82f6' },
    { name: 'Technical', value: visibilityMetrics?.technicalOptimization, fill: '#10b981' },
    { name: 'Citation', value: visibilityMetrics?.citationLikelihood, fill: '#8b5cf6' },
    { name: 'Freshness', value: visibilityMetrics?.freshness, fill: '#ec4899' }
  ].filter((m) => typeof m.value === 'number');

  // Prepare content optimization pie chart data
  const contentPieData = [
    { name: 'Structured Data', value: contentOptimization?.structuredData || 0 },
    { name: 'Content Depth', value: contentOptimization?.contentDepth || 0 },
    { name: 'Topic Coverage', value: contentOptimization?.topicCoverage || 0 },
    { name: 'E-E-A-T', value: contentOptimization?.eeatSignals || 0 }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'platforms', label: 'Platform Scores', icon: CpuChipIcon },
    { id: 'keywords', label: 'Keyword Visibility', icon: LightBulbIcon },
  ];

  return (
    <div className="space-y-8">
      {/* Overall Score Header */}
      <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-8">
        <div className="flex flex-col md:flex-row items-center gap-8">
          <div className="relative">
                {typeof overallScore === 'number'
                  ? <ScoreGauge score={overallScore} size="lg" />
                  : (
                    <div style={{ textAlign: 'center', padding: '24px 12px' }}>
                      <div style={{ color: '#6b7280', fontSize: 34, fontWeight: 700, lineHeight: 1 }}>&mdash;</div>
                      <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 8 }}>Overall score not measured</div>
                    </div>
                  )}
          </div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Visibility Snapshot</h2>
            <p className="text-gray-600 mb-1">Domain: <span className="text-orange-400 font-medium">{domain}</span></p>
            <p className="text-gray-500 text-sm">Analyzed: {snapshotData.timestamp ? new Date(snapshotData.timestamp).toLocaleString() : 'Not yet analyzed'}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {serpPresence && (
                <>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${serpPresence.hasAIOverview ? 'bg-green-900/50 text-green-400 border border-green-700' : 'bg-gray-100 text-gray-500 border border-gray-300'}`}>
                    {serpPresence.hasAIOverview ? '' : ''} AI Overview
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${serpPresence.hasFeaturedSnippet ? 'bg-green-900/50 text-green-400 border border-green-700' : 'bg-gray-100 text-gray-500 border border-gray-300'}`}>
                    {serpPresence.hasFeaturedSnippet ? '' : ''} Featured Snippet
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${serpPresence.hasKnowledgePanel ? 'bg-green-900/50 text-green-400 border border-green-700' : 'bg-gray-100 text-gray-500 border border-gray-300'}`}>
                    {serpPresence.hasKnowledgePanel ? '' : ''} Knowledge Panel
                  </span>
                </>
              )}
            </div>
          </div>
          {/* Mini platform scores */}
          <div className="grid grid-cols-3 gap-3">
            {platformRows.map(({ key, score: value }) => (
              <div key={key} className="bg-gray-50 rounded-lg p-3 text-center min-w-[90px]">
                <div className="text-lg font-bold" style={{ color: PLATFORM_COLORS[key] || '#f97316' }}>{typeof value === 'number' ? value : 'N/A'}</div>
                <div className="text-xs text-gray-600">{PLATFORM_NAMES[key] || key}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200 pb-0">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-orange-500 text-orange-400'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Radar Chart - Platform Comparison */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Platform Visibility</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {platformRows.map((p) => (
              <div key={p.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', background: '#0d1117', border: '1px solid #1f2937', borderRadius: 8 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#e5e7eb', fontSize: 15, fontWeight: 600 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: PLATFORM_ROW_COLORS[p.key] || '#6366f1' }} />
                  {p.name}
                </span>
                {typeof p.score === 'number' ? (
                  <span style={{ color: '#e5e7eb', fontSize: 15, fontWeight: 700 }}>
                    {p.score}<span style={{ color: '#6b7280', fontWeight: 400 }}>/100</span>
                  </span>
                ) : (
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Not available</span>
                )}
              </div>
            ))}
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
              No public interface reports brand visibility inside these assistants.
              Rows marked &ldquo;Not available&rdquo; are unmeasured &mdash; not zero, and not an estimate.
            </div>
          </div>
          </div>

          {/* Bar Chart - Visibility Metrics */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Visibility Metrics</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={metricsBarData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" domain={[0, 100]} tick={{ fill: '#d1d5db' }} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#d1d5db', fontSize: 15 }} width={100} />
                <Tooltip content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div style={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '10px', color: '#fff' }}>
                          <p style={{ margin: 0, color: '#fff' }}>{label}</p>
                          {payload.map((entry, index) => (
                            <p key={index} style={{ margin: '4px 0 0 0', color: '#fff' }}>{entry.name} : {entry.value}</p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {metricsBarData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Content Optimization Donut */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Optimization</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={contentPieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={5} dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}>
                  {contentPieData.map((entry, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div style={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '10px', color: '#fff' }}>
                          <p style={{ margin: 0, color: '#fff' }}>{label}</p>
                          {payload.map((entry, index) => (
                            <p key={index} style={{ margin: '4px 0 0 0', color: '#fff' }}>{entry.name} : {entry.value}</p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Recommendations */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommendations</h3>
            <div className="space-y-3">
              {(recommendations || []).map((rec, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <LightBulbIcon className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{rec}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'platforms' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {platformRows.map(({ key, score: value }) => {
            const color = PLATFORM_COLORS[key] || '#f97316';
            const level = typeof value !== 'number' ? 'Not available' : value >= 70 ? 'Strong' : value >= 40 ? 'Moderate' : 'Weak';
            const levelColor = typeof value !== 'number' ? 'text-gray-400' : value >= 70 ? 'text-green-400' : value >= 40 ? 'text-yellow-400' : 'text-red-400';
            return (
              <div key={key} className="bg-white border border-gray-200 shadow-sm rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-gray-900 font-semibold">{PLATFORM_NAMES[key] || key}</h4>
                  <span className={`text-xs font-medium ${levelColor}`}>{level}</span>
                </div>
                <div className="mb-3">
                  <div className="flex items-end gap-2">
                    <span className="text-3xl font-bold" style={{ color }}>{typeof value === 'number' ? value : '—'}</span>
                    <span className="text-gray-500 text-sm mb-1">/100</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className="h-2 rounded-full transition-all duration-1000" style={{ width: `${typeof value === 'number' ? value : 0}%`, backgroundColor: color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'keywords' && (
        <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Keyword Visibility Analysis</h3>
            <p className="text-gray-600 text-sm mt-1">
              {rankingKeywordsSummary?.totalKeywords || 0} total keywords tracked &bull; {rankingKeywordsSummary?.top10Count || 0} in top 10
            </p>
          </div>
          
          {/* Keyword bar chart */}
          {keywordVisibility && keywordVisibility.length > 0 && (
            <div className="p-6 border-b border-gray-200">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={keywordVisibility}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="keyword" tick={{ fill: '#d1d5db', fontSize: 14 }} angle={-20} textAnchor="end" height={60} />
                  <YAxis domain={[0, 100]} tick={{ fill: '#d1d5db' }} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div style={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', padding: '10px', color: '#fff' }}>
                          <p style={{ margin: 0, color: '#fff' }}>{label}</p>
                          {payload.map((entry, index) => (
                            <p key={index} style={{ margin: '4px 0 0 0', color: '#fff' }}>{entry.name} : {entry.value}</p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Bar dataKey="visibility" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          
          {/* Keyword table */}
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-gray-600 text-xs font-medium uppercase px-6 py-3">Keyword</th>
                <th className="text-center text-gray-600 text-xs font-medium uppercase px-4 py-3">Position</th>
                <th className="text-center text-gray-600 text-xs font-medium uppercase px-4 py-3">Visibility</th>
                <th className="text-center text-gray-600 text-xs font-medium uppercase px-4 py-3">Trend</th>
              </tr>
            </thead>
            <tbody>
              {(keywordVisibility || []).map((kw, i) => (
                <tr key={i} className="border-b border-gray-200/50 hover:bg-gray-100">
                  <td className="px-6 py-3 text-gray-900 text-sm">{kw.keyword}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-medium ${kw.position <= 10 ? 'text-green-400' : kw.position <= 30 ? 'text-yellow-400' : 'text-red-400'}`}>
                      #{kw.position}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-16 bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full bg-orange-500" style={{ width: `${kw.visibility}%` }} />
                      </div>
                      <span className="text-sm text-gray-600">{kw.visibility}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center"><TrendIcon trend={kw.trend} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AISearchVisibility;
